/**
 * Impersonation Event Bus
 *
 * Mirrors `platform/auth/services/session-events.ts`: a module-level pub/sub so
 * the HTTP layer can say "the impersonated session is over" without importing
 * React or ImpersonationContext (which would be a cycle).
 *
 * Wiring contract:
 *  - `api.service` emits when a request carrying the IMPERSONATED bearer comes
 *    back 401 — the session expired or a super-admin terminated it.
 *  - `ImpersonationContext` subscribes, drops the impersonated identity, and
 *    shows the admin a `role="alert"` saying they are back in their own
 *    session. Returning them there in silence is the failure this prevents.
 */

export type ImpersonationEndedListener = () => void

const listenersRegistry = new Set<ImpersonationEndedListener>()

export function subscribeToImpersonationRejected(listener: ImpersonationEndedListener): () => void {
  listenersRegistry.add(listener)
  return () => {
    listenersRegistry.delete(listener)
  }
}

/** Fire-and-forget. Listeners must defend themselves; one throwing must not
 *  break the HTTP error path or starve the others. */
export function emitImpersonationRejected(): void {
  for (const listener of listenersRegistry) {
    try {
      listener()
    } catch {
      // Intentionally swallowed — see above.
    }
  }
}
